import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type HeadMeta = {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
};

type HeadMetaContextValue = HeadMeta & {
  setTitle: (title?: string) => void;
  setDescription: (description?: string) => void;
  setKeywords: (keywords?: string) => void;
  setOgImage: (ogImage?: string) => void;
  setMeta: (meta: Partial<HeadMeta>) => void;
};

const HeadMetaContext = createContext<HeadMetaContextValue | undefined>(undefined);

export function HeadMetaProvider({ children }: { children: React.ReactNode }) {
  const [meta, setMetaState] = useState<HeadMeta>({});

  const setMeta = useCallback((patch: Partial<HeadMeta>) => {
    setMetaState(prev => ({ ...prev, ...patch }));
  }, []);

  const setTitle = useCallback((title?: string) => setMeta({ title }), [setMeta]);
  const setDescription = useCallback((description?: string) => setMeta({ description }), [setMeta]);
  const setKeywords = useCallback((keywords?: string) => setMeta({ keywords }), [setMeta]);
  const setOgImage = useCallback((ogImage?: string) => setMeta({ ogImage }), [setMeta]);

  const value = useMemo<HeadMetaContextValue>(
    () => ({ ...meta, setTitle, setDescription, setKeywords, setOgImage, setMeta }),
    [meta, setTitle, setDescription, setKeywords, setOgImage, setMeta]
  );

  return <HeadMetaContext.Provider value={value}>{children}</HeadMetaContext.Provider>;
}

export function useHeadMeta(): HeadMetaContextValue {
  const ctx = useContext(HeadMetaContext);
  if (!ctx) throw new Error('useHeadMeta must be used within HeadMetaProvider');
  return ctx;
}
